import React, { ComponentProps, ReactNode, useState } from 'react';
import { Popover, Typography } from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space16 } from 'glints-aries/es/@next/utilities/spacing';
import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';

import { getConfig } from '../../../../config';
import {
  ContentContainer,
  CustomPopoverStyles,
  HeaderContainer,
  StyledImage,
  StyledVIPBadge,
} from './VIPBenefitPopover.sc';

type CallbackWithPopoverActions<T> = (_: {
  openPopover: () => void;
  closePopover: () => void;
}) => T;
type VIPBenefitPopoverProps = {
  title: ReactNode;
  description: ReactNode;
  images?: {
    id: string;
    en: string;
  };
  activator: CallbackWithPopoverActions<JSX.Element>;
  onMouseEnter?: CallbackWithPopoverActions<void>;
  onMouseLeave?: CallbackWithPopoverActions<void>;
  preferredPosition?: ComponentProps<typeof Popover>['preferredPosition'];
};

const VIPBadge = () => (
  <StyledVIPBadge>
    <img
      width={space16}
      height={space16}
      src="/images/credit-system/vip-icon-sm.svg"
      alt="VIP Icon"
    />
    <Typography variant="overline" color={Neutral.B18}>
      <FormattedMessage id="text-glints-vip" defaultMessage="GLINTS VIP" />
    </Typography>
  </StyledVIPBadge>
);

export default function VIPBenefitPopover({
  description,
  title,
  activator,
  onMouseEnter,
  onMouseLeave,
  images,
  preferredPosition = 'mostSpace',
}: VIPBenefitPopoverProps) {
  const [isPopoverActive, setIsPopoverActive] = useState(false);
  const [openPopover, closePopover] = [
    () => setIsPopoverActive(true),
    () => setIsPopoverActive(false),
  ];
  const [makeMouseEnterCallback, makeMouseLeaveCallback] = [
    () => onMouseEnter && onMouseEnter({ openPopover, closePopover }),
    () => onMouseLeave && onMouseLeave({ openPopover, closePopover }),
  ];
  const lang = (getConfig().LANG || 'en').toLowerCase();
  const country = getConfig().COUNTRY?.toLowerCase();

  const localizationLang =
    country && country !== lang ? `${lang}-${country}` : lang;

  const image = get(images, localizationLang, images?.en);

  return (
    <div
      onMouseEnter={makeMouseEnterCallback}
      onMouseLeave={makeMouseLeaveCallback}
      style={{
        width: 'max-content',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <CustomPopoverStyles />
      <Popover
        active={isPopoverActive}
        activator={activator({ openPopover, closePopover })}
        autofocusTarget="first-node"
        onClose={closePopover}
        preferredAlignment={'left'}
        zIndexOverride={1001}
        preferredPosition={preferredPosition}
      >
        <Popover.Pane>
          <ContentContainer>
            <VIPBadge />
            <HeaderContainer>
              <Typography variant="subtitle1">{title}</Typography>
              <Typography variant="body1">{description}</Typography>
            </HeaderContainer>
            {image && (
              <a
                href={image}
                target="_blank"
                style={{
                  display: 'contents',
                }}
                rel="noreferrer"
              >
                <StyledImage src={image} />
              </a>
            )}
          </ContentContainer>
        </Popover.Pane>
      </Popover>
    </div>
  );
}
